/* eslint-disable no-param-reassign */
import { useState } from 'react';
import cloneDeep from 'lodash.clonedeep';
import uuidv4 from '../../juristec-ui/utils/functions/randomUUID';

import {
  GroupOptions,
  TypeSimpleOptions,
  TypePeriodOptions,
  TypeDateOptions,
  TypeNumberOptions,
  TypeTextOptions,
} from './options';
import { dateOptions } from '../../options';
import {
  getOptionsByGroup,
  getActualTypeAndInverse,
  getSelectorByType,
  formatValue,
  ftypeRebuild,
  emptyOpt,
} from './utils';

import type { SelectOption } from '../../types';
import type {
  Value,
  DataFrameTypes,
  ModifierConfig,
  Modifier,
  ModifierGroup,
  UseKpiModifiers,
  UseModifierConfig,
  ValueContain,
  ValueBetween,
  ValueHandler,
  RawValue,
  ColumnHandler,
} from './types';

const emptyModifierConfig: ModifierConfig = {
  column: undefined,
  selector: dateOptions[0],
  group: GroupOptions.map.simple,
  format: TypeSimpleOptions.map.values,
  rule: '',
  values: [],
  origin: undefined,
};

const regexFloat = /^-{0,1}\d*\.{0,1}\d*/;
const regexInt = /^\d*/;

const emptyFunc = () => {};

export const columnIsDate = (
  dataFrameTypes: DataFrameTypes, columnOption?: Pick<SelectOption, 'value'>,
) => (
  Boolean(columnOption) && ['datetime64[ns]', 'date'].includes(dataFrameTypes[columnOption!.value])
);

export const useKpiModifiers = ({
  dataFrameTypes,
  initialModifiers,
  onModifierChange = emptyFunc,
}: UseKpiModifiers) => {
  const [modifiers, setModifiers] = useState<ModifierGroup>(initialModifiers);

  const addModifier = (modifierConfig: ModifierConfig) => {
    if (!modifierConfig.column) return false;

    const columnType = dataFrameTypes[modifierConfig.column.value];
    const [isInverse, actualType] = getActualTypeAndInverse(modifierConfig.format.value);
    const isSimple = modifierConfig.group.value === GroupOptions.enum.SIMPLE;
    const isDate = columnIsDate(dataFrameTypes, modifierConfig.column);
    const selector = isSimple ? (modifierConfig?.selector?.value || '') : getSelectorByType(modifierConfig.format.value);
    const origin = modifierConfig.origin?.value ?? 'modifier';

    const configObj: Modifier = {
      uid: uuidv4(),
      column: modifierConfig.column.value,
      type: columnType,
      ftype: actualType || TypeSimpleOptions.enum.VALUES,
      selector: isDate ? selector : '',
      values: modifierConfig.values.reduce((aux, v) => {
        const val = formatValue(v, actualType, isDate, isSimple);
        if (val || val === 0) aux.push(val);
        return aux;
      }, [] as Value[]),
      inverse: isInverse,
      rule: modifierConfig.rule,
    };

    const newModifiers = {
      ...modifiers,
      [origin]: [...(modifiers[origin] ?? []), cloneDeep(configObj)],
    };
    setModifiers(newModifiers);
    onModifierChange(newModifiers, origin, 'add');
    return true;
  };

  const removeModifier = (origin: string, uid: string) => {
    const newModifiers = {
      ...modifiers,
      [origin]: modifiers[origin].filter((m) => m.uid !== uid),
    };
    setModifiers(newModifiers);
    onModifierChange(newModifiers, origin, 'remove');
  };

  const editModifier = (
    origin: string, uid: string, newValues: Value[], fType: string, newRule: string = '',
  ) => {
    if (newValues.length === 0) return;
    const old = modifiers[origin].find((m) => m.uid === uid);
    if (!old) return;
    const configObj: Modifier = {
      ...old,
      values: newValues.reduce((aux, v) => {
        const val = formatValue(
          v, fType,
          columnIsDate(dataFrameTypes, { value: old.column }),
          fType === TypeSimpleOptions.enum.VALUES,
        );
        if (val || val === 0) aux.push(val);
        return aux;
      }, [] as Value[]),
      rule: newRule.trim(),
    };
    const newModifiers = {
      ...modifiers,
      [origin]: modifiers[origin].map((m) => (
        m.uid === uid ? configObj : m
      )),
    };
    setModifiers(newModifiers);
    onModifierChange(newModifiers, origin, 'edit');
  };

  const changeModifierOrder = (origin: string, pos: number, varOp: 1|-1) => {
    const newPos = pos + varOp;
    if (newPos < 0 || newPos >= modifiers[origin].length) return;
    const tempList = [...modifiers[origin]];
    const [auxItem] = tempList.splice(pos, 1);
    tempList.splice(newPos, 0, auxItem);
    const newModifiers = {
      ...modifiers,
      [origin]: tempList,
    };
    setModifiers(newModifiers);
    onModifierChange(newModifiers, origin, 'order');
  };

  const resetModifiers = (keep: string[] = []) => {
    setModifiers((old) => Object.entries(old).reduce((aux, [k, m]) => {
      if (keep.includes(k)) aux[k] = m;
      else aux[k] = [];
      return aux;
    }, {} as ModifierGroup));
  };

  return {
    modifiers,
    setModifiers,
    addModifier,
    removeModifier,
    editModifier,
    changeModifierOrder,
    resetModifiers,
  };
};

export const useModifierConfig = ({
  dataFrameTypes,
  modifierOrigins,
  getUniqueValues,
}: UseModifierConfig) => {
  const [modifierConfig, setModifierConfig] = useState<ModifierConfig>(emptyModifierConfig);
  const [simpleOptions, setSimpleOptions] = useState<SelectOption[]>([]);
  const [originOptions, setOriginOptions] = useState<SelectOption[]>([]);

  const configColumnIsDate = () => columnIsDate(dataFrameTypes, modifierConfig.column);

  const getOptionsByConfigGroup = () => getOptionsByGroup(
    modifierConfig.group.value === GroupOptions.enum.SIMPLE, modifierConfig.column?.type,
  );

  const getColumnOptions = (columns: ColumnHandler) => {
    const opts: Record<string, SelectOption> = {};
    Object.entries(columns).forEach(([k, v]) => {
      v.forEach((o) => {
        const aux = opts[o.value];
        if (aux) aux.origin = [...(aux.origin ?? []), k];
        else opts[o.value] = { ...o, origin: [k] };
      });
    });
    return Object.values(opts);
  };

  const handleValuesOptions = (
    columnOption?: SelectOption,
    selectorOption?: SelectOption,
    groupOption?: SelectOption,
  ) => {
    if (groupOption?.value === GroupOptions.enum.ADVANCED) return;
    let selector = selectorOption?.value || '';
    if (!columnIsDate(dataFrameTypes, columnOption)) selector = '';
    const column = columnOption?.value;
    if (!column) return;
    getUniqueValues(column, selector).then((opts) => {
      setSimpleOptions(opts);
    });
  };

  const getSimpleOptions = (modifiers: ModifierGroup) => {
    const modifsWithouConfig = modifiers[
      modifierConfig.origin?.value ?? ''
    ]?.filter((m) => (
      m.uid !== modifierConfig.uid && modifierConfig.column?.value === m.column
    )) || [];
    return simpleOptions.filter((opt) => (
      !modifsWithouConfig.some((m) => m.values.includes(opt.value))
    ));
  };

  const handleModifierValues = (value: ValueHandler): Value[] => {
    switch (modifierConfig.format.value) {
      case TypeSimpleOptions.enum.NOT_VALUES:
      case TypeSimpleOptions.enum.VALUES: {
        const val = value as SelectOption[];
        if (!val) return [];
        if (val.length === 0) return [];
        if (val[0].value === 'select_all') return simpleOptions;
        if (val.length > 0) return val;
        return [];
      }
      case TypeTextOptions.enum.NOT_CONTAINS:
      case TypeTextOptions.enum.CONTAINS: {
        const val = value as ValueContain;
        const auxVals = new Set(modifierConfig.values);
        auxVals[val.operation](val.val);
        return [...auxVals];
      }
      case TypeDateOptions.enum.NOT_BETWEEN:
      case TypeNumberOptions.enum.NOT_BETWEEN:
      case TypeDateOptions.enum.BETWEEN:
      case TypeNumberOptions.enum.BETWEEN:
      case TypeDateOptions.enum.NOT_BETWEENINC:
      case TypeNumberOptions.enum.NOT_BETWEENINC:
      case TypeDateOptions.enum.BETWEENINC:
      case TypeNumberOptions.enum.BETWEENINC: {
        const val = value as ValueBetween;
        let val1 = val.start;
        let val2 = val.end;
        if (!configColumnIsDate()) {
          [val1] = val.start?.toString()?.replace(',', '.')?.match(regexFloat) || '';
          [val2] = val.end?.toString()?.replace(',', '.')?.match(regexFloat) || '';
        }
        return [
          val1 || (val1 !== '' ? modifierConfig.values[0] : ''),
          val2 || (val2 !== '' ? modifierConfig.values[1] : ''),
        ];
      }
      case 'not-before':
      case TypeDateOptions.enum.BEFORE:
      case 'not-before_inc':
      case TypeDateOptions.enum.BEFOREINC:
      case 'not-after':
      case TypeDateOptions.enum.AFTER:
      case 'not-after_inc':
      case TypeDateOptions.enum.AFTERINC:
      case TypeDateOptions.enum.SPECIFICDATE: {
        const val = value as number | Date;
        return [val];
      }
      case TypePeriodOptions.enum.CURRENTMONTH:
      case TypePeriodOptions.enum.CURRENTYEAR:
      case TypePeriodOptions.enum.CURRENTSEMESTER:
      case TypePeriodOptions.enum.CURRENTQUARTER:
      case TypePeriodOptions.enum.CURRENTWEEK:
        return [];
      default: {
        let val = value as string | number;
        val = val.toString().match(regexInt)?.[0] || '';
        return [val];
      }
    }
  };

  const handleModifierConfig = <K extends keyof ModifierConfig>(
    key: K, value: unknown,
  ) => {
    const newConfig: Partial<ModifierConfig> = {};
    switch (key) {
      case 'group': {
        const val = value as ModifierConfig['group'];
        newConfig.group = val;
        newConfig.values = [];
        if (val.value === GroupOptions.enum.SIMPLE) {
          newConfig.format = TypeSimpleOptions.map.values;
        } else {
          newConfig.format = emptyOpt;
        }
        break;
      }
      case 'format': {
        const val = value as ModifierConfig['format'];
        newConfig.format = val;
        switch (val.value) {
          case TypeTextOptions.enum.NOT_CONTAINS:
          case TypeTextOptions.enum.CONTAINS:
          case TypeSimpleOptions.enum.NOT_VALUES:
          case TypeSimpleOptions.enum.VALUES:
            newConfig.values = [];
            break;
          case TypeDateOptions.enum.SPECIFICDATE:
            newConfig.values = [{ value: 'hoje', label: 'Hoje', id: 'today' }];
            break;
          case TypeDateOptions.enum.NOT_BETWEEN:
          case TypeNumberOptions.enum.NOT_BETWEEN:
          case TypeDateOptions.enum.BETWEEN:
          case TypeNumberOptions.enum.BETWEEN:
          case TypeDateOptions.enum.NOT_BETWEENINC:
          case TypeNumberOptions.enum.NOT_BETWEENINC:
          case TypeDateOptions.enum.BETWEENINC:
          case TypeNumberOptions.enum.BETWEENINC:
            if (configColumnIsDate()) {
              newConfig.values = [new Date(), new Date()];
            } else {
              newConfig.values = ['', ''];
            }
            break;
          case 'not-before':
          case TypeDateOptions.enum.BEFORE:
          case 'not-before_inc':
          case TypeDateOptions.enum.BEFOREINC:
          case 'not-after':
          case TypeDateOptions.enum.AFTER:
          case 'not-after_inc':
          case TypeDateOptions.enum.AFTERINC:
            newConfig.values = [new Date()];
            break;
          default:
            newConfig.values = [''];
            break;
        }
        break;
      }
      case 'column': {
        const val = value as ModifierConfig['column'];
        newConfig.column = val;
        if (modifierConfig.group.value === GroupOptions.enum.SIMPLE) {
          newConfig.format = TypeSimpleOptions.map.values;
          newConfig.values = [];
        } else newConfig.format = emptyOpt;
        if (val) {
          const originOpts = val.origin?.map((o: string) => ({
            value: o, label: modifierOrigins[o], id: o,
          })) || [];
          setOriginOptions(originOpts);
          [newConfig.origin] = originOpts;
        }
        break;
      }
      case 'values': {
        const val = value as ValueHandler;
        newConfig.values = handleModifierValues(val);
        break;
      }
      case 'selector':
      case 'origin': {
        const val = value as ModifierConfig[K];
        newConfig[key] = val;
        newConfig.values = [];
        break;
      }
      default:
        newConfig[key] = value as ModifierConfig[K];
        break;
    }
    return newConfig;
  };

  const handleModifierConfigs = (newData: Partial<ModifierConfig>) => {
    let aux = cloneDeep(modifierConfig);
    let loadValuesOptions = false;
    (Object.entries(newData) as [keyof ModifierConfig, ModifierConfig[keyof ModifierConfig]][])
      .forEach(([k, v]) => {
        if (['column', 'selector', 'origin', 'group'].includes(k)) loadValuesOptions = true;
        aux = {
          ...aux,
          ...handleModifierConfig<typeof k>(k, v),
        };
      });
    if (loadValuesOptions) {
      handleValuesOptions(
        aux.column ?? modifierConfig.column,
        aux.selector ?? modifierConfig.selector,
        aux.group ?? modifierConfig.group,
      );
    }
    setModifierConfig(aux);
  };

  const handleRawValues = (values: RawValue[], ftype: string, column: string) => {
    switch (ftype) {
      case TypeSimpleOptions.enum.VALUES: {
        const vals = values as string[];
        return vals.map((v) => ({ label: v, value: v, id: v }));
      }
      case TypeDateOptions.enum.SPECIFICDATE: {
        const vals = values as string[];
        return vals.map((v) => ({
          label: v.charAt(0).toUpperCase() + v.substring(1), value: v, id: v,
        }));
      }
      case TypePeriodOptions.enum.CURRENTMONTH:
      case TypePeriodOptions.enum.CURRENTYEAR:
      case TypePeriodOptions.enum.CURRENTWEEK:
      case TypePeriodOptions.enum.CURRENTQUARTER:
      case TypePeriodOptions.enum.CURRENTSEMESTER: {
        return [];
      }
      case TypeDateOptions.enum.BEFORE:
      case TypeDateOptions.enum.BEFOREINC:
      case TypeDateOptions.enum.AFTER:
      case TypeDateOptions.enum.AFTERINC:
      case TypeDateOptions.enum.BETWEEN:
      case TypeDateOptions.enum.BETWEENINC: {
        if (columnIsDate(dataFrameTypes, { value: column })) {
          const vals = values as string[];
          return vals.map((v) => new Date(`${v} 00:00:00`));
        } return values.map((v) => v.toString().match(regexInt)?.[0] || '');
      }
      case TypeTextOptions.enum.CONTAINS: {
        const vals = values as string[];
        return [...(new Set(vals))];
      }
      default: {
        const vals = values as (string | number)[];
        return vals.map((v) => v.toString().match(regexInt)?.[0] || '');
      }
    }
  };

  const fromModifierToConfig = (origin: string, modifier: Modifier) => {
    const auxConfig: Partial<ModifierConfig> = {
      uid: modifier.uid,
      column: { label: modifier.column, value: modifier.column, id: modifier.column },
      selector: modifier.selector ? {
        label: modifier.selector, value: modifier.selector, id: modifier.selector,
      } : undefined,
      group: GroupOptions.map[
        modifier.ftype === TypeSimpleOptions.enum.VALUES
          ? GroupOptions.enum.SIMPLE : GroupOptions.enum.ADVANCED
      ],
      rule: modifier.rule || '',
      origin: { label: modifierOrigins[origin], value: origin, id: origin },
    };
    auxConfig.format = ftypeRebuild(
      modifier.ftype,
      modifier.selector,
      columnIsDate(dataFrameTypes, auxConfig.column),
      modifier.inverse,
    );
    auxConfig.values = handleRawValues(
      modifier.values as RawValue[], modifier.ftype, modifier.column,
    );
    handleValuesOptions(auxConfig.column, auxConfig.selector, auxConfig.group);
    setModifierConfig(auxConfig as Required<ModifierConfig>);
  };

  const checkModifierValues = () => {
    switch (modifierConfig.format.value) {
      case TypeTextOptions.enum.NOT_CONTAINS:
      case TypeTextOptions.enum.CONTAINS:
      case TypeSimpleOptions.enum.NOT_VALUES:
      case TypeSimpleOptions.enum.VALUES:
      case TypeDateOptions.enum.SPECIFICDATE:
        return modifierConfig.values.length > 0;
      case TypeDateOptions.enum.NOT_BETWEEN:
      case TypeNumberOptions.enum.NOT_BETWEEN:
      case TypeDateOptions.enum.BETWEEN:
      case TypeNumberOptions.enum.BETWEEN:
      case TypeDateOptions.enum.NOT_BETWEENINC:
      case TypeNumberOptions.enum.NOT_BETWEENINC:
      case TypeDateOptions.enum.BETWEENINC:
      case TypeNumberOptions.enum.BETWEENINC: {
        let [val1, val2] = modifierConfig.values;
        if (!configColumnIsDate()) {
          if (Boolean(val1) && Boolean(val2)) {
            val1 = Number(modifierConfig.values[0]);
            val2 = Number(modifierConfig.values[1]);
          } else {
            return false;
          }
        }
        return !Number.isNaN(val1) && !Number.isNaN(val2) && val2 > val1;
      }
      case 'not-before':
      case TypeDateOptions.enum.BEFORE:
      case 'not-before_inc':
      case TypeDateOptions.enum.BEFOREINC:
      case 'not-after':
      case TypeDateOptions.enum.AFTER:
      case 'not-after_inc':
      case TypeDateOptions.enum.AFTERINC:
        return modifierConfig.values[0] instanceof Date;
      case TypePeriodOptions.enum.CURRENTMONTH:
      case TypePeriodOptions.enum.CURRENTYEAR:
      case TypePeriodOptions.enum.CURRENTSEMESTER:
      case TypePeriodOptions.enum.CURRENTQUARTER:
      case TypePeriodOptions.enum.CURRENTWEEK:
        return true;
      default: {
        if (modifierConfig.values.length > 0 && modifierConfig.values[0] !== '') {
          const val = Number(modifierConfig.values[0]);
          return !Number.isNaN(val);
        }
        return false;
      }
    }
  };

  const resetModifierConfigs = (keep: (keyof ModifierConfig)[] = []) => {
    setModifierConfig((old) => ({
      ...emptyModifierConfig,
      ...Object.fromEntries(keep.map((k) => [k, old[k]])),
    }));
  };

  return {
    // Modifier state and handlers
    modifierConfig,
    simpleOptions,
    originOptions,
    getSimpleOptions,
    getColumnOptions,
    handleModifierConfigs,
    resetModifierConfigs,
    fromModifierToConfig,

    // Utility functions
    configColumnIsDate,
    getOptionsByConfigGroup,
    checkModifierValues,
  };
};
